/**
 * Dashboard Widget Configuration
 * Defines available widgets and their default settings
 */

export const WIDGET_TYPES = {
  STATS_OVERVIEW: 'stats_overview',
  WORK_ORDER_CHART: 'work_order_chart',
  ASSET_CATEGORIES: 'asset_categories',
  RECENT_ACTIVITY: 'recent_activity',
  UPCOMING_MAINTENANCE: 'upcoming_maintenance',
  OVERDUE_ITEMS: 'overdue_items',
  QUICK_ACTIONS: 'quick_actions',
  SLA_COMPLIANCE: 'sla_compliance',
  SAVED_VIEWS: 'saved_views',
  SAVED_VIEWS_ADMIN_USAGE: 'saved_views_admin_usage',
};

export const WIDGET_CONFIG = {
  [WIDGET_TYPES.STATS_OVERVIEW]: {
    id: WIDGET_TYPES.STATS_OVERVIEW,
    title: 'Statistics Overview',
    description: 'Key metrics at a glance',
    minWidth: 2,
    minHeight: 1,
    defaultWidth: 6,
    defaultHeight: 1,
    icon: 'BarChart3',
  },
  [WIDGET_TYPES.WORK_ORDER_CHART]: {
    id: WIDGET_TYPES.WORK_ORDER_CHART,
    title: 'Work Order Status',
    description: 'Bar chart showing work order distribution by status',
    minWidth: 1,
    minHeight: 2,
    defaultWidth: 3,
    defaultHeight: 2,
    icon: 'TrendingUp',
  },
  [WIDGET_TYPES.ASSET_CATEGORIES]: {
    id: WIDGET_TYPES.ASSET_CATEGORIES,
    title: 'Asset Categories',
    description: 'Pie chart of assets by category',
    minWidth: 1,
    minHeight: 2,
    defaultWidth: 3,
    defaultHeight: 2,
    icon: 'PieChart',
  },
  [WIDGET_TYPES.RECENT_ACTIVITY]: {
    id: WIDGET_TYPES.RECENT_ACTIVITY,
    title: 'Recent Activity',
    description: 'Latest work orders and inspections',
    minWidth: 2,
    minHeight: 2,
    defaultWidth: 6,
    defaultHeight: 2,
    icon: 'Activity',
  },
  [WIDGET_TYPES.UPCOMING_MAINTENANCE]: {
    id: WIDGET_TYPES.UPCOMING_MAINTENANCE,
    title: 'Upcoming Maintenance',
    description: 'Assets requiring maintenance soon',
    minWidth: 1,
    minHeight: 2,
    defaultWidth: 3,
    defaultHeight: 2,
    icon: 'Wrench',
  },
  [WIDGET_TYPES.OVERDUE_ITEMS]: {
    id: WIDGET_TYPES.OVERDUE_ITEMS,
    title: 'Overdue Items',
    description: 'Work orders and inspections past due date',
    minWidth: 1,
    minHeight: 2,
    defaultWidth: 3,
    defaultHeight: 2,
    icon: 'AlertTriangle',
  },
  [WIDGET_TYPES.QUICK_ACTIONS]: {
    id: WIDGET_TYPES.QUICK_ACTIONS,
    title: 'Quick Actions',
    description: 'Shortcuts to common tasks',
    minWidth: 1,
    minHeight: 1,
    defaultWidth: 2,
    defaultHeight: 1,
    icon: 'Zap',
  },
  [WIDGET_TYPES.SLA_COMPLIANCE]: {
    id: WIDGET_TYPES.SLA_COMPLIANCE,
    title: 'SLA Compliance',
    description: 'Service level agreement performance',
    minWidth: 1,
    minHeight: 1,
    defaultWidth: 2,
    defaultHeight: 1,
    icon: 'Target',
  },
  [WIDGET_TYPES.SAVED_VIEWS]: {
    id: WIDGET_TYPES.SAVED_VIEWS,
    title: 'Saved Views',
    description: 'Your top 3 most-used filter shortcuts',
    minWidth: 2,
    minHeight: 2,
    defaultWidth: 3,
    defaultHeight: 2,
    icon: 'Bookmark',
  },
  [WIDGET_TYPES.SAVED_VIEWS_ADMIN_USAGE]: {
    id: WIDGET_TYPES.SAVED_VIEWS_ADMIN_USAGE,
    title: 'Org Saved-View Usage (Admin)',
    description: 'Most-applied saved views across the team',
    minWidth: 2,
    minHeight: 2,
    defaultWidth: 3,
    defaultHeight: 2,
    icon: 'BarChart3',
  },
};

// Default layout for new users
export const DEFAULT_LAYOUT = [
  { i: WIDGET_TYPES.STATS_OVERVIEW, x: 0, y: 0, w: 6, h: 1 },
  { i: WIDGET_TYPES.WORK_ORDER_CHART, x: 0, y: 1, w: 3, h: 2 },
  { i: WIDGET_TYPES.ASSET_CATEGORIES, x: 3, y: 1, w: 3, h: 2 },
  { i: WIDGET_TYPES.SAVED_VIEWS, x: 0, y: 3, w: 3, h: 2 },
  { i: WIDGET_TYPES.RECENT_ACTIVITY, x: 3, y: 3, w: 3, h: 2 },
];

// Default enabled widgets
export const DEFAULT_ENABLED_WIDGETS = [
  WIDGET_TYPES.STATS_OVERVIEW,
  WIDGET_TYPES.WORK_ORDER_CHART,
  WIDGET_TYPES.ASSET_CATEGORIES,
  WIDGET_TYPES.SAVED_VIEWS,
  WIDGET_TYPES.RECENT_ACTIVITY,
];

/**
 * Get saved layout from localStorage
 */
export const getSavedLayout = () => {
  try {
    const saved = localStorage.getItem('dashboard_layout');
    return saved ? JSON.parse(saved) : null;
  } catch {
    return null;
  }
};

/**
 * Save layout to localStorage
 */
export const saveLayout = (layout) => {
  try {
    localStorage.setItem('dashboard_layout', JSON.stringify(layout));
  } catch (e) {
    console.error('Failed to save layout:', e);
  }
};

/**
 * Get enabled widgets from localStorage
 */
export const getEnabledWidgets = () => {
  try {
    const saved = localStorage.getItem('dashboard_widgets');
    return saved ? JSON.parse(saved) : DEFAULT_ENABLED_WIDGETS;
  } catch {
    return DEFAULT_ENABLED_WIDGETS;
  }
};

/**
 * Save enabled widgets to localStorage
 */
export const saveEnabledWidgets = (widgets) => {
  try {
    localStorage.setItem('dashboard_widgets', JSON.stringify(widgets));
  } catch (e) {
    console.error('Failed to save widgets:', e);
  }
};

/**
 * Reset to default layout
 */
export const resetToDefault = () => {
  localStorage.removeItem('dashboard_layout');
  localStorage.removeItem('dashboard_widgets');
};
